import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import {
  isTekMeghaAndroidApp,
  readTekMeghaFcmToken
} from '../utils/tekmegha-android-bridge.util';

type UpsertResult = {
  success?: boolean;
  message?: string;
  id?: number;
};

/**
 * Registers TekMegha Android FCM tokens in Supabase (`tekmegha_device_tokens`)
 * via `upsert_tekmegha_fcm_token`. Firebase project: tekmegha-supa-fcm.
 */
@Injectable({ providedIn: 'root' })
export class TekMeghaFcmTokenService {
  private readonly supabase = inject(SupabaseService);
  private lastSyncedToken: string | null = null;
  private syncInFlight: Promise<boolean> | null = null;

  /** Call after login / session restore when running inside the Android app. */
  async syncTokenForCurrentUser(storeCode?: string | null): Promise<boolean> {
    if (!isTekMeghaAndroidApp()) {
      return false;
    }

    const token = await this.waitForToken();
    if (!token) {
      return false;
    }

    if (this.lastSyncedToken === token && this.syncInFlight == null) {
      return true;
    }

    if (this.syncInFlight) {
      return this.syncInFlight;
    }

    this.syncInFlight = this.upsertToken(token, storeCode)
      .then(ok => {
        if (ok) this.lastSyncedToken = token;
        return ok;
      })
      .finally(() => {
        this.syncInFlight = null;
      });

    return this.syncInFlight;
  }

  /**
   * Local reset on logout. Row delete requires an authenticated session
   * (`delete_tekmegha_fcm_token`); after SIGNED_OUT that RPC cannot run.
   * Stale tokens are cleaned when FCM reports them invalid on send.
   */
  clearLocalSyncState(): void {
    this.lastSyncedToken = null;
  }

  /** Call while still signed in (e.g. before signOut) to remove this device token. */
  async deleteTokenWhileAuthenticated(): Promise<void> {
    if (!isTekMeghaAndroidApp()) return;
    const token = readTekMeghaFcmToken();
    if (!token) return;

    try {
      await this.supabase.client.rpc('delete_tekmegha_fcm_token', {
        p_fcm_token: token
      });
    } catch {
      /* non-blocking */
    } finally {
      this.lastSyncedToken = null;
    }
  }

  private async upsertToken(token: string, storeCode?: string | null): Promise<boolean> {
    try {
      const { data, error } = await this.supabase.client.rpc('upsert_tekmegha_fcm_token', {
        p_fcm_token: token,
        p_platform: 'android',
        p_store_code: storeCode?.trim() || null,
        p_app_id: 'com.tekmegha.digital'
      });

      if (error) {
        console.warn('[TekMeghaFCM] upsert failed', error.message);
        return false;
      }

      const result = data as UpsertResult | null;
      if (result && result.success === false) {
        console.warn('[TekMeghaFCM] upsert rejected', result.message);
        return false;
      }

      return true;
    } catch (e) {
      console.warn('[TekMeghaFCM] upsert error', e);
      return false;
    }
  }

  /** Token may arrive slightly after WebView inject; retry briefly. */
  private async waitForToken(attempts = 8, delayMs = 400): Promise<string | null> {
    for (let i = 0; i < attempts; i++) {
      const token = readTekMeghaFcmToken();
      if (token) return token;
      await new Promise(r => setTimeout(r, delayMs));
    }
    return readTekMeghaFcmToken();
  }
}
